import $ from "jquery";
import assert from "minimalistic-assert";

import * as channel from "./channel.ts";
import {$t} from "./i18n.ts";
import * as markdown from "./markdown.ts";
import * as people from "./people.ts";
import * as ui_report from "./ui_report.ts";

type InsigniaControl = {
    name: string;
    type: string;
    default_value: string | number | boolean;
    help_text: string;
};

type Insignia = {
    insignia_name: string;
    name: string;
    description: string;
    md_content: string;
    controls: InsigniaControl[];
    enabled?: boolean;
    control_values?: Record<string, string | number | boolean>;
};

let available_insignia: Insignia[] = [];
let current_editing_insignia: string | null = null;

export function set_up(): void {
    // Load available insignia
    load_insignia();

    // Set up event handlers
    setup_event_handlers();
}

function render_insignia_preview(): void {
    const current_user_id = people.my_current_user_id();
    const current_user = people.get_by_user_id(current_user_id);
    const enabled_badges = available_insignia.filter((b) => b.enabled);
    
    // Helper function to substitute $variable$ placeholders with control values
    function substitute_variables(markdown: string, control_values: Record<string, string | number | boolean>): string {
        let result = markdown;
        for (const [control_name, control_value] of Object.entries(control_values)) {
            // Replace $control_name$ with the actual value
            const placeholder = `$${control_name}$`;
            result = result.replace(new RegExp(placeholder.replace(/\$/g, '\\$'), 'g'), String(control_value));
        }
        return result;
    }
    
    // Build the message content with insignia markdown prepended
    let message_content = "This is a sample message.";
    for (const insignia of enabled_badges) {
        let insignia_markdown = insignia.md_content.trim();
        // Substitute control variable placeholders with user's configured values
        if (insignia.control_values) {
            insignia_markdown = substitute_variables(insignia_markdown, insignia.control_values);
        }
        message_content = insignia_markdown + " " + message_content;
    }
    
    // Get avatar URL or use a default
    const avatar_url = current_user.avatar_url ?? "/static/images/default-avatar.svg";
    
    // Send to server for rendering
    channel.post({
        url: "/json/users/me/insignia_preview",
        data: {insignia_markdown_content: message_content},
        success(data) {
            const preview_html = `
                <div class="message-preview">
                    <div class="inline-profile-picture-wrapper">
                        <div class="inline_profile_picture" style="position: relative; overflow: hidden;">
                            <img src="${avatar_url}" class="preview-avatar" alt="${current_user.full_name}" style="position: relative; z-index: 1;">
                        </div>
                    </div>
                    <div class="message-content">
                        <div class="message-header">
                            <span class="sender-name">${current_user.full_name}</span>
                        </div>
                        <div class="message-text rendered_markdown">
                            ${data.rendered}
                        </div>
                    </div>
                </div>
            `;
            
            $("#insignia-preview-message").html(preview_html);
            
            // Handle avatar overlays if present
            if (data.avatar_overlays && data.avatar_overlays.length > 0) {
                const $avatar_container = $("#insignia-preview-message").find(".inline_profile_picture");
                // Apply avatar overlays using the same transform-based approach as real messages
                for (const overlay of data.avatar_overlays) {
                    const $overlay = $(`<img src="${overlay.url}" class="avatar-overlay">`);
                    // Use transform-based positioning to match real message avatars
                    const transform = `translate(calc(-50% + ${overlay.x}px), calc(-50% + ${overlay.y}px)) scale(${overlay.size}) rotate(${overlay.rot}deg)`;
                    $overlay.css({
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: transform,
                        opacity: overlay.opacity / 100,
                        zIndex: overlay.z >= 1 ? overlay.z + 1 : 0,
                        pointerEvents: "none",
                        maxWidth: "300px",
                        maxHeight: "300px",
                    });
                    if (overlay.desc) {
                        $overlay.attr("title", overlay.desc);
                    }
                    $avatar_container.append($overlay);
                }
            }
        },
        error() {
            // Fallback to client-side rendering if server fails
            const rendered = markdown.render(message_content);
            const preview_html = `
                <div class="message-preview">
                    <img src="${avatar_url}" class="preview-avatar" alt="${current_user.full_name}">
                    <div class="message-content">
                        <div class="message-header">
                            <span class="sender-name">${current_user.full_name}</span>
                        </div>
                        <div class="message-text rendered_markdown">
                            ${rendered.content}
                        </div>
                    </div>
                </div>
            `;
            $("#insignia-preview-message").html(preview_html);
        },
    });
}

function load_insignia(): void {
    const $list = $("#available-insignia-list");
    $list.html('<div class="loading-indicator"><i class="fa fa-spinner fa-spin"></i> ' + $t({defaultMessage: "Loading insignia..."}) + "</div>");
    
    channel.get({
        url: "/json/users/me/insignia",
        success(data) {
            available_insignia = data.insignia;
            load_user_settings();
        },
        error(xhr) {
            $list.html("");
            ui_report.error($t({defaultMessage: "Error loading insignia"}), xhr, $("#insignia-status"));
        },
    });
}

function load_user_settings(): void {
    channel.get({
        url: "/json/users/me/insignia_settings",
        success(data) {
            // Merge user settings with available insignia
            const settings_map = new Map(
                data.insignia_settings.map((s: {
                    insignia_name: string;
                    enabled: boolean;
                    control_values: Record<string, string | number | boolean>;
                }) => [s.insignia_name, s]),
            );

            for (const insignia of available_insignia) {
                const user_setting = settings_map.get(insignia.insignia_name);
                if (user_setting) {
                    insignia.enabled = user_setting.enabled;
                    insignia.control_values = user_setting.control_values;
                } else {
                    insignia.enabled = false;
                    insignia.control_values = {};
                }
            }

            render_insignia_list();
        },
        error(xhr) {
            $("#available-insignia-list").html("");
            ui_report.error(
                $t({defaultMessage: "Error loading insignia settings"}),
                xhr,
                $("#insignia-status"),
            );
        },
    });
}

function render_insignia_list(): void {
    const $list = $("#available-insignia-list");
    
    if (available_insignia.length === 0) {
        $list.html('<div class="no-badges-message">' + $t({defaultMessage: "No insignia are available for your account."}) + "</div>");
        return;
    }

    let html = "";
    for (const insignia of available_insignia) {
        const checked = insignia.enabled ? "checked" : "";
        const status_class = insignia.enabled ? "insignia-enabled" : "insignia-disabled";
        const status_text = insignia.enabled ? $t({defaultMessage: "Enabled"}) : $t({defaultMessage: "Disabled"});
        
        // Build controls HTML
        let controls_html = "";
        if (insignia.controls && insignia.controls.length > 0) {
            controls_html = '<div class="insignia-inline-controls">';
            for (const control of insignia.controls) {
                const current_value = insignia.control_values?.[control.name] ?? control.default_value ?? "";
                const control_id = `control-${insignia.insignia_name}-${control.name}`;
                
                controls_html += `<div class="control-group">`;
                controls_html += `<label for="${control_id}">${control.name}</label>`;
                
                if (control.type === "bool") {
                    const checkedAttr = current_value ? "checked" : "";
                    controls_html += `<input type="checkbox" id="${control_id}" class="insignia-control" data-insignia-name="${insignia.insignia_name}" data-control-name="${control.name}" ${checkedAttr}>`;
                } else if (control.type === "int") {
                    controls_html += `<input type="number" step="1" id="${control_id}" class="insignia-control" data-insignia-name="${insignia.insignia_name}" data-control-name="${control.name}" value="${current_value}">`;
                } else if (control.type === "float") {
                    controls_html += `<input type="number" step="any" id="${control_id}" class="insignia-control" data-insignia-name="${insignia.insignia_name}" data-control-name="${control.name}" value="${current_value}">`;
                } else if (control.type === "text") {
                    controls_html += `<textarea id="${control_id}" class="insignia-control" data-insignia-name="${insignia.insignia_name}" data-control-name="${control.name}" rows="3">${current_value}</textarea>`;
                } else {
                    controls_html += `<input type="text" id="${control_id}" class="insignia-control" data-insignia-name="${insignia.insignia_name}" data-control-name="${control.name}" value="${current_value}">`;
                }
                
                if (control.help_text) {
                    controls_html += `<p class="help-text">${control.help_text}</p>`;
                }
                
                controls_html += `</div>`;
            }
            controls_html += '<button class="button small rounded sea-green save-insignia-controls" data-insignia-name="' + insignia.insignia_name + '">' + $t({defaultMessage: "Save"}) + '</button>';
            controls_html += '</div>';
        }
        
        html += `
            <div class="insignia-card ${status_class}" data-insignia-name="${insignia.insignia_name}">
                <div class="insignia-info">
                    <div class="insignia-name" data-insignia-name="${insignia.insignia_name}">${insignia.name}</div>
                    <div class="insignia-description">${insignia.description}</div>
                </div>
                <div class="insignia-controls">
                    <label class="checkbox">
                        <input type="checkbox" class="insignia-enabled-toggle" data-insignia-name="${insignia.insignia_name}" ${checked}>
                        <span class="insignia-status-text">${status_text}</span>
                    </label>
                </div>
                ${controls_html}
            </div>
        `;
    }
    
    $list.html(html);
    
    // Update preview
    render_insignia_preview();
}

function setup_event_handlers(): void {
    // Handle insignia enable/disable toggles
    $("#insignia-settings").on("change", ".insignia-enabled-toggle", function (this: HTMLInputElement, e) {
        e.preventDefault();
        const $checkbox = $(this);
        const insignia_name = $checkbox.data("insignia-name") as string;
        const enabled = this.checked;
        
        // Store original state in case we need to revert
        const original_state = !enabled;

        update_insignia_setting(insignia_name, {enabled}, $checkbox, original_state);
    });

    // Handle clicking insignia name to toggle
    $("#insignia-settings").on("click", ".insignia-name", function () {
        const insignia_name = $(this).data("insignia-name") as string;
        const $checkbox = $(`.insignia-enabled-toggle[data-insignia-name="${CSS.escape(insignia_name)}"]`);
        $checkbox.prop("checked", !$checkbox.prop("checked")).trigger("change");
    });

    // Handle inline control changes
    $("#insignia-settings").on("change", ".insignia-control", function () {
        const $control = $(this);
        const insignia_name = $control.data("insignia-name") as string;
        const control_name = $control.data("control-name") as string;
        
        const insignia = available_insignia.find((b) => b.insignia_name === insignia_name);
        if (!insignia) return;
        
        // Update the local value
        const control_def = insignia.controls.find((c) => c.name === control_name);
        if (!control_def) return;
        
        let value: string | number | boolean;
        if (control_def.type === "bool") {
            value = ($control as JQuery<HTMLInputElement>).prop("checked") as boolean;
        } else if (control_def.type === "int") {
            value = parseInt(($control as JQuery<HTMLInputElement>).val() as string, 10);
        } else if (control_def.type === "float") {
            value = parseFloat(($control as JQuery<HTMLInputElement>).val() as string);
        } else {
            value = ($control as JQuery<HTMLInputElement>).val() as string;
        }
        
        if (!insignia.control_values) {
            insignia.control_values = {};
        }
        insignia.control_values[control_name] = value;
        
        // Update preview immediately
        render_insignia_preview();
    });

    // Handle save controls button
    $("#insignia-settings").on("click", ".save-insignia-controls", function () {
        const insignia_name = $(this).data("insignia-name") as string;
        const insignia = available_insignia.find((b) => b.insignia_name === insignia_name);
        if (!insignia) return;
        
        // Collect current values from controls
        const control_values: Record<string, string | number | boolean> = {};
        $(`.insignia-control[data-insignia-name="${CSS.escape(insignia_name)}"]`).each(function () {
            const $control = $(this);
            const control_name = $control.data("control-name") as string;
            const control_def = insignia.controls.find((c) => c.name === control_name);
            if (!control_def) return;
            
            let value: string | number | boolean;
            if (control_def.type === "bool") {
                value = ($control as JQuery<HTMLInputElement>).prop("checked") as boolean;
            } else if (control_def.type === "int") {
                value = parseInt(($control as JQuery<HTMLInputElement>).val() as string, 10);
            } else if (control_def.type === "float") {
                value = parseFloat(($control as JQuery<HTMLInputElement>).val() as string);
            } else {
                value = ($control as JQuery<HTMLInputElement>).val() as string;
            }
            control_values[control_name] = value;
        });
        
        // Update on server
        update_insignia_setting(insignia_name, {control_values});
    });

    // Handle edit controls button (legacy modal - keep for backwards compatibility)
    $("#insignia-settings").on("click", ".edit-insignia-controls", function () {
        const insignia_name = $(this).data("insignia-name") as string;
        open_insignia_controls_modal(insignia_name);
    });

    // Handle save button in modal
    $("#save-insignia-controls").on("click", () => {
        save_insignia_controls();
    });
}

function update_insignia_setting(
    insignia_name: string,
    updates: {enabled?: boolean; control_values?: Record<string, string | number | boolean>},
    $checkbox?: JQuery<HTMLInputElement>,
    original_checkbox_state?: boolean,
): void {
    const data: Record<string, unknown> = {
        insignia_name,
    };
    
    if (updates.enabled !== undefined) {
        data.enabled = JSON.stringify(updates.enabled);
    }
    if (updates.control_values !== undefined) {
        data.control_values = JSON.stringify(updates.control_values);
    }
    
    console.log("[Insignia] Updating insignia setting:", {insignia_name, updates, data});
    
    channel.patch({
        url: "/json/users/me/insignia_settings",
        data,
        success(response_data) {
            console.log("[Insignia] Update successful:", response_data);
            
            // Update local state
            const insignia = available_insignia.find((b) => b.insignia_name === insignia_name);
            if (insignia) {
                if (updates.enabled !== undefined) {
                    insignia.enabled = updates.enabled;
                    console.log("[Insignia] Local state updated - enabled:", insignia.enabled);
                    
                    // Update the status text and card class in the UI
                    if ($checkbox) {
                        const $card = $checkbox.closest(".insignia-card");
                        const $statusText = $checkbox.closest(".insignia-controls").find(".insignia-status-text");
                        
                        if (updates.enabled) {
                            $card.removeClass("insignia-disabled").addClass("insignia-enabled");
                            $statusText.text($t({defaultMessage: "Enabled"}));
                        } else {
                            $card.removeClass("insignia-enabled").addClass("insignia-disabled");
                            $statusText.text($t({defaultMessage: "Disabled"}));
                        }
                    }
                }
                if (updates.control_values !== undefined) {
                    insignia.control_values = updates.control_values;
                }
            } else {
                console.warn("[Insignia] Insignia not found in local state:", insignia_name);
            }
            
            // Update preview
            render_insignia_preview();
            
            // Show success message
            ui_report.success(
                $t({defaultMessage: "Insignia settings updated"}),
                $("#insignia-status"),
            );
        },
        error(xhr) {
            console.error("[Insignia] Update failed:", {
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText,
                responseJSON: xhr.responseJSON,
            });
            
            // Try to get a meaningful error message
            let error_msg = $t({defaultMessage: "Error updating insignia settings"});
            if (xhr.responseJSON && xhr.responseJSON.msg) {
                error_msg = xhr.responseJSON.msg;
            }
            
            ui_report.error(
                error_msg,
                xhr,
                $("#insignia-status"),
            );
            
            // Revert the checkbox state if provided
            if ($checkbox && original_checkbox_state !== undefined) {
                console.log("[Insignia] Reverting checkbox to:", original_checkbox_state);
                $checkbox.prop("checked", original_checkbox_state);
            }
            // Also reload user settings to ensure consistency
            load_user_settings();
        },
    });
}

function open_insignia_controls_modal(insignia_name: string): void {
    const insignia = available_insignia.find((b) => b.insignia_name === insignia_name);
    assert(insignia !== undefined);

    current_editing_insignia = insignia_name;

    // Set modal title
    $("#insignia-modal-name").text(insignia.name);

    // Build form for controls
    const $form = $("#insignia-controls-form");
    $form.empty();

    for (const control of insignia.controls) {
        const current_value =
            insignia.control_values?.[control.name] ?? control.default_value ?? "";
        const $control_group = $("<div>").addClass("control-group");

        const $label = $("<label>").text(control.name);
        $control_group.append($label);

        let $input: JQuery;
        switch (control.type) {
            case "bool":
                $input = $("<input>")
                    .attr("type", "checkbox")
                    .attr("data-control-name", control.name)
                    .prop("checked", Boolean(current_value));
                break;
            case "int":
                $input = $("<input>")
                    .attr("type", "number")
                    .attr("step", "1")
                    .attr("data-control-name", control.name)
                    .val(String(current_value));
                break;
            case "float":
                $input = $("<input>")
                    .attr("type", "number")
                    .attr("step", "any")
                    .attr("data-control-name", control.name)
                    .val(String(current_value));
                break;
            case "text":
                // Multi-line text area
                $input = $("<textarea>")
                    .attr("data-control-name", control.name)
                    .attr("rows", "4")
                    .val(String(current_value));
                break;
            default:
                // str - single line text
                $input = $("<input>")
                    .attr("type", "text")
                    .attr("data-control-name", control.name)
                    .val(String(current_value));
                break;
        }

        $control_group.append($input);

        if (control.help_text) {
            const $help = $("<p>").addClass("help-text").text(control.help_text);
            $control_group.append($help);
        }

        $form.append($control_group);
    }

    // Show modal
    $("#insignia-controls-modal").modal("show");
}

function save_insignia_controls(): void {
    assert(current_editing_insignia !== null);
    const insignia = available_insignia.find((b) => b.insignia_name === current_editing_insignia);
    assert(insignia !== undefined);

    const control_values: Record<string, string | number | boolean> = {};

    // Collect values from form
    for (const control of insignia.controls) {
        const $input = $(`#insignia-controls-form [data-control-name="${CSS.escape(control.name)}"]`);

        let value: string | number | boolean;
        if (control.type === "bool") {
            value = ($input as JQuery<HTMLInputElement>).prop("checked") as boolean;
        } else if (control.type === "int") {
            value = Number.parseInt($input.val() as string, 10);
        } else if (control.type === "float") {
            value = Number.parseFloat($input.val() as string);
        } else {
            value = $input.val() as string;
        }

        control_values[control.name] = value;
    }

    // Update insignia setting
    update_insignia_setting(current_editing_insignia, {control_values});

    // Close modal
    $("#insignia-controls-modal").modal("hide");
    current_editing_insignia = null;
    
    // Update preview after a short delay to let the modal close
    setTimeout(() => {
        render_insignia_preview();
    }, 300);
}
